/**
 * Utilidades para manejo de fechas
 * Formato estándar en UI: DD/MM/YYYY
 */

/**
 * Convierte fecha DD/MM/YYYY → YYYY-MM-DD (para input type="date")
 * @param {string} fecha - Fecha en formato DD/MM/YYYY
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const toInputDate = (fecha) => {
  if (!fecha) return '';
  
  try {
    // Detectar separador (/ o -)
    const separador = fecha.includes('/') ? '/' : '-';
    const [dia, mes, año] = fecha.split(separador);
    
    if (!dia || !mes || !año) {
      console.warn('⚠️ Fecha inválida:', fecha);
      return '';
    }
    
    return `${año}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
  } catch (e) {
    console.error('❌ Error convirtiendo fecha a input:', e);
    return '';
  }
};

/**
 * Convierte fecha YYYY-MM-DD → DD/MM/YYYY (para mostrar en UI)
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @returns {string} Fecha en formato DD/MM/YYYY
 */
export const toDisplayDate = (fecha) => {
  if (!fecha) return '';
  
  try {
    const [año, mes, dia] = fecha.split('-');
    
    if (!dia || !mes || !año) {
      console.warn('⚠️ Fecha inválida:', fecha);
      return '';
    }
    
    return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${año}`;
  } catch (e) {
    console.error('❌ Error convirtiendo fecha a display:', e);
    return '';
  }
};

/**
 * Convierte fecha DD/MM/YYYY → DD-MM-YYYY (para backend Farme)
 * @param {string} fecha - Fecha en formato DD/MM/YYYY
 * @returns {string} Fecha en formato DD-MM-YYYY
 */
export const toBackendDate = (fecha) => {
  if (!fecha) return '';
  
  try {
    return fecha.replace(/\//g, '-');
  } catch (e) {
    console.error('❌ Error convirtiendo fecha a backend:', e);
    return '';
  }
};

/**
 * Convierte fecha DD-MM-YYYY → DD/MM/YYYY (desde backend Farme)
 * @param {string} fecha - Fecha en formato DD-MM-YYYY
 * @returns {string} Fecha en formato DD/MM/YYYY
 */
export const fromBackendDate = (fecha) => {
  if (!fecha) return '';
  
  try {
    return fecha.replace(/-/g, '/');
  } catch (e) {
    console.error('❌ Error convirtiendo fecha desde backend:', e);
    return '';
  }
};

/**
 * Valida formato de fecha DD/MM/YYYY
 * @param {string} fecha - Fecha a validar
 * @returns {boolean} true si es válida
 */
export const isValidDate = (fecha) => {
  if (!fecha) return false;
  
  try {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = fecha.match(regex);
    
    if (!match) return false;
    
    const [, dia, mes, año] = match;
    const date = new Date(año, mes - 1, dia);
    
    return (
      date.getFullYear() === parseInt(año) &&
      date.getMonth() === parseInt(mes) - 1 &&
      date.getDate() === parseInt(dia)
    );
  } catch (e) {
    return false;
  }
};

/**
 * Obtiene fecha actual en formato DD/MM/YYYY
 * @returns {string} Fecha actual
 */
export const getCurrentDate = () => {
  const hoy = new Date();
  const dia = String(hoy.getDate()).padStart(2, '0');
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  const año = hoy.getFullYear();
  
  return `${dia}/${mes}/${año}`;
};

/**
 * Formatea fecha para Siscotel (verificar formato requerido)
 * @param {string} fecha - Fecha en formato DD/MM/YYYY
 * @returns {string} Fecha en formato requerido por Siscotel
 */
export const toSiscotelDate = (fecha) => {
  // TODO: Verificar formato exacto requerido por Siscotel
  // Por ahora, usar formato ISO
  if (!fecha) return '';
  
  try {
    const [dia, mes, año] = fecha.split('/');
    return `${año}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
  } catch (e) {
    console.error('❌ Error convirtiendo fecha a Siscotel:', e);
    return '';
  }
};

/**
 * Parsea fecha desde Siscotel (verificar formato recibido)
 * @param {string} fecha - Fecha desde Siscotel
 * @returns {string} Fecha en formato DD/MM/YYYY
 */
export const fromSiscotelDate = (fecha) => {
  // TODO: Verificar formato exacto recibido de Siscotel
  // Por ahora, asumir formato ISO
  if (!fecha) return '';
  
  try {
    const [año, mes, dia] = fecha.split('-');
    return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${año}`;
  } catch (e) {
    console.error('❌ Error parseando fecha desde Siscotel:', e);
    return '';
  }
};
